/* =========================================================
   <sticky-atc>
   Self-contained custom element. Drops in alongside the main
   product info; doesn't depend on global.js's bindPDPSelectEvents
   so initialization is deterministic and idempotent.

   Open/close pattern mirrors the main PDP picker: toggling
   .active on the .select element animates max-height open,
   then .visible flips overflow + visibility so the dropdown
   actually appears. We delay removing .visible by 750ms to
   allow the max-height transition to finish (matches main).

   Variant changes mirror into the main <select> + dispatch
   change, so product-info.js handles the variant fetch.
   Desktop ATC click proxies to the main ATC button.
   Mobile ATC click smooth-scrolls to the main ATC.
   ========================================================= */

if (!customElements.get("sticky-atc")) {
  customElements.define(
    "sticky-atc",
    class StickyAtc extends HTMLElement {
      constructor() {
        super();
        this.sectionId = this.dataset.section;
        this.targetId = this.dataset.target;
        this.productFormId = this.dataset.productFormId;

        this.mobileBreakpoint = 990;
        this.transitionMs = 750; // matches main picker's close timing
        this.isVisible = false;
        this.unsubscribeVariantChange = null;
        this.mainPickerSyncAbortController = null;
        this.buttonStateObserver = null;
        this.debug = this.isDebugEnabled();
        this.variantData = null;

        // Track pending close timers per dropdown so we can cancel them
        // when the same dropdown is reopened. A WeakMap keyed by the
        // .select element keeps cleanup automatic when nodes are removed.
        this._closeTimers = new WeakMap();

        this._onDocClick = this._onDocClick.bind(this);
        this._refreshStickyState = this._refreshStickyState.bind(this);
      }

      connectedCallback() {
        this.setupVariantChangeListener();
        requestAnimationFrame(() => this.init());
      }

      disconnectedCallback() {
        this.observer?.disconnect();
        document.removeEventListener("click", this._onDocClick);
        if (typeof this.unsubscribeVariantChange === "function") {
          this.unsubscribeVariantChange();
        }
        if (this.mainPickerSyncAbortController) {
          this.mainPickerSyncAbortController.abort();
        }
        if (this.buttonStateObserver) {
          this.buttonStateObserver.disconnect();
        }
        window.removeEventListener("load", this._refreshStickyState);
        window.removeEventListener("pageshow", this._refreshStickyState);
        window.removeEventListener("resize", this._refreshStickyState);
        // window.removeEventListener("scroll", this._refreshStickyState);
      }

      init() {
        this.targetEl = this.findTargetEl();
        if (!this.targetEl) return;

        this.logDebug("init", this.getDebugState());

        this.removeAttribute("hidden");
        this.classList.add("sticky-atc--ready");
        this.setAttribute("aria-hidden", "true");

        this.setupObserver();
        this.setupSubmit();
        this.setupMainButtonObserver();
        this.setupVariantDropdowns();
        this.setupVirtualVariantDropdown();
        this.setupMainPickerDelegatedSync();
        this.syncStickyFromMainPicker();
        this.syncCurrentVariantToMainForm("init");
        this.syncFromMainButton();

        document.addEventListener("click", this._onDocClick);
      }

      findTargetEl() {
        const submitBtn = document.getElementById(this.targetId);
        if (!submitBtn) return null;
        return submitBtn.closest(".product-form__buttons") || submitBtn;
      }

      /* ---------- Show / hide on scroll ---------- */

      setupObserver() {
        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              const scrolledPast = entry.boundingClientRect.bottom < 0;
              const shouldShow = !entry.isIntersecting && scrolledPast;
              this.toggleVisibility(shouldShow);
            });
          },
          { root: null, threshold: 0 }
        );

        this.observer.observe(this.targetEl);

        // IntersectionObserver can miss the restored scroll position on
        // mobile reload/back-forward cache. Force a direct position check
        // during init and again after the browser finishes restoring scroll.
        this._refreshStickyState();
        requestAnimationFrame(this._refreshStickyState);
        window.setTimeout(this._refreshStickyState, 250);
        window.setTimeout(this._refreshStickyState, 750);
        window.setTimeout(this._refreshStickyState, 1500);
        window.setTimeout(this._refreshStickyState, 2500);
        window.addEventListener("load", this._refreshStickyState, { once: true });
        window.addEventListener("pageshow", this._refreshStickyState);
        window.addEventListener("resize", this._refreshStickyState, { passive: true });
        // window.addEventListener("scroll", this._refreshStickyState, { passive: true });
      }

      _refreshStickyState() {
        this.updateVisibilityFromTarget();
        this.syncFromMainButton();
      }

      updateVisibilityFromTarget() {
        if (!this.targetEl) return;

        const rect = this.targetEl.getBoundingClientRect();
        const shouldShow = rect.bottom < 0;

        this.toggleVisibility(shouldShow);
      }

      toggleVisibility(shouldShow) {
        if (shouldShow === this.isVisible) return;

        this.isVisible = shouldShow;
        this.classList.toggle("is-visible", shouldShow);
        this.setAttribute("aria-hidden", shouldShow ? "false" : "true");
        document.body.classList.toggle("sticky-atc-visible", shouldShow);

        if (shouldShow) {
          this.syncFromMainButton();
        } else {
          this.closeAllDropdowns(true);
        }
      }

      isMobile() {
        return window.matchMedia(
          `(max-width: ${this.mobileBreakpoint - 1}px)`
        ).matches;
      }

      /* ---------- Submit button ---------- */

      setupSubmit() {
        const submitBtn = this.querySelector("[data-sticky-atc-submit]");
        if (!submitBtn) return;

        submitBtn.addEventListener("click", (e) => {
          e.preventDefault();
          this.logDebug("submit click", this.getDebugState());
          if (this.isMobile()) {
            this.scrollToMain();
            return;
          }
          const liveBtn = document.getElementById(this.targetId);
          if (liveBtn && !liveBtn.disabled && liveBtn.style.display !== "none") {
            liveBtn.click();
          }
        });
      }

      setupMainButtonObserver() {
        const liveBtn = document.getElementById(this.targetId);
        if (!liveBtn || typeof MutationObserver === "undefined") return;

        if (this.buttonStateObserver) {
          this.buttonStateObserver.disconnect();
        }

        this.buttonStateObserver = new MutationObserver(() => {
          requestAnimationFrame(() => this.syncFromMainButton());
        });

        this.buttonStateObserver.observe(liveBtn, {
          attributes: true,
          attributeFilter: ["disabled", "aria-disabled", "style", "class"],
          childList: true,
          subtree: true,
          characterData: true,
        });
      }

      scrollToMain() {
        const candidates = [
          document.getElementById(`variant-selects-${this.sectionId}`),
          document.getElementById(`variant-selects-virtual-${this.sectionId}`),
        ].filter(Boolean);

        let anchorEl = this.targetEl;
        if (candidates.length) {
          anchorEl = candidates.reduce((highest, el) =>
            el.getBoundingClientRect().top < highest.getBoundingClientRect().top
              ? el
              : highest
          );
        }
        if (!anchorEl) return;

        const headerOffset = 80; // fixed site header height
        const topSpacing = 40;   // *** extra space above the pickers — raise for more room ***
        const rect = anchorEl.getBoundingClientRect();
        const y = rect.top + window.pageYOffset - headerOffset - topSpacing;
        window.scrollTo({ top: Math.max(0, y), behavior: "smooth" });
      }

      /* ---------- Regular variant dropdowns ----------
         Each [data-sticky-dropdown] is a .select element. We toggle
         .active + .visible on it (same pattern as the main picker)
         and let base.css drive the open animation. */

      setupVariantDropdowns() {
        const selectEls = Array.from(
          this.querySelectorAll(".select[data-sticky-dropdown]")
        );

        selectEls.forEach((selectEl) => {
          const nativeSelect = selectEl.querySelector("select");
          const customOptions = Array.from(
            selectEl.querySelectorAll(".custom-option")
          );
          let highlightIndex = -1;

          // Click the .select wrap to toggle. Use capture phase +
          // stopImmediatePropagation so any global handler (e.g. one
          // bound by bindPDPSelectEvents on .product-form__input .select
          // elements) cannot also fire and immediately re-close us.
          // Ignore clicks on individual options (handled separately).
          selectEl.addEventListener(
            "click",
            (e) => {
              if (e.target.closest(".custom-option")) return;
              e.stopPropagation();
              e.stopImmediatePropagation();
              const isOpen = selectEl.classList.contains("active");
              this.closeAllDropdowns();
              if (!isOpen) this.openSelect(selectEl);
            },
            true // capture phase
          );

          // Keyboard
          selectEl.addEventListener("keydown", (e) => {
            if (e.key === "Tab") return;
            const enabled = customOptions.filter(
              (o) => !o.classList.contains("unavailable")
            );
            if (!enabled.length) return;

            if (e.key === "ArrowDown") {
              e.preventDefault();
              highlightIndex = (highlightIndex + 1) % enabled.length;
              this.highlight(enabled, highlightIndex);
              if (!selectEl.classList.contains("active")) this.openSelect(selectEl);
            } else if (e.key === "ArrowUp") {
              e.preventDefault();
              highlightIndex =
                (highlightIndex - 1 + enabled.length) % enabled.length;
              this.highlight(enabled, highlightIndex);
              if (!selectEl.classList.contains("active")) this.openSelect(selectEl);
            } else if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              if (highlightIndex >= 0 && selectEl.classList.contains("active")) {
                enabled[highlightIndex].click();
              } else {
                this.openSelect(selectEl);
              }
            } else if (e.key === "Escape") {
              this.closeSelect(selectEl);
            }
          });

          // Option clicks — capture phase + stopImmediatePropagation
          // so global PDP dropdown logic can't process the same click
          // and close us before our selection logic runs.
          customOptions.forEach((opt) => {
            opt.addEventListener(
              "click",
              (e) => {
                e.preventDefault();
                e.stopPropagation();
                e.stopImmediatePropagation();
                if (opt.classList.contains("unavailable")) return;

                const realValue = opt.dataset.realValue || opt.dataset.value;

                // Update sticky's own visual state
                customOptions.forEach((o) => o.classList.remove("selected"));
                opt.classList.add("selected");

                // Update sticky's visible label
                this.updateStickyLabel(selectEl.parentElement, opt, realValue);

                // Sync the native <select> inside the sticky (kept as
                // the form value carrier even though it's hidden)
                if (nativeSelect) {
                  this.syncSelectValue(nativeSelect, realValue);
                }

                // Push the choice into the main picker so
                // product-info.js does its normal variant fetch.
                this.mirrorToMainPicker(nativeSelect, e);

                this.closeSelect(selectEl);
              },
              true // capture phase
            );
          });
        });
      }

      openSelect(selectEl) {
        // CRITICAL: cancel any pending close timer for this dropdown.
        // Without this, a previous close() call's timer would later
        // remove .visible from the dropdown we just reopened.
        this._cancelCloseTimer(selectEl);
        selectEl.classList.add("active", "visible");
      }

      closeSelect(selectEl) {
        // Cancel any existing pending close for this element first so
        // we don't stack two timers (the second one could fire after a
        // subsequent reopen).
        this._cancelCloseTimer(selectEl);
        selectEl.classList.remove("active");
        // Match main picker timing — keep .visible briefly so the
        // collapse animation can finish before hiding. Track the
        // timer so a re-open can cancel it.
        const t = setTimeout(() => {
          // Only strip .visible if the dropdown wasn't reopened in
          // the meantime. If .active is back, the user reopened it.
          if (!selectEl.classList.contains("active")) {
            selectEl.classList.remove("visible");
          }
          this._closeTimers.delete(selectEl);
        }, this.transitionMs);
        this._closeTimers.set(selectEl, t);
      }

      closeAllDropdowns(instant = false) {
        this.querySelectorAll(".select.active, .select.visible").forEach((sel) => {
          this._cancelCloseTimer(sel);
          sel.classList.remove("active");
          if (instant) {
            sel.classList.remove("visible");
          } else {
            const t = setTimeout(() => {
              if (!sel.classList.contains("active")) {
                sel.classList.remove("visible");
              }
              this._closeTimers.delete(sel);
            }, this.transitionMs);
            this._closeTimers.set(sel, t);
          }
        });
      }

      _cancelCloseTimer(selectEl) {
        const t = this._closeTimers.get(selectEl);
        if (t) {
          clearTimeout(t);
          this._closeTimers.delete(selectEl);
        }
      }

      highlight(opts, index) {
        opts.forEach((o, i) => {
          o.classList.toggle("highlight", i === index);
          if (i === index) o.scrollIntoView({ block: "nearest" });
        });
      }

      updateStickyLabel(cellEl, optionEl, label) {
        const labelContainer = cellEl.querySelector(":scope > .custom__label");
        if (!labelContainer) return;

        // Rebuild the label: optional color-dot + text
        const dot = optionEl.querySelector(".color-dot");
        labelContainer.innerHTML = "";
        if (dot) labelContainer.appendChild(dot.cloneNode(true));
        labelContainer.appendChild(document.createTextNode(label));
      }

      mirrorToMainPicker(stickySelect, sourceEvent) {
        if (!stickySelect) return;

        const index = this.getSelectIndex(stickySelect);
        if (index === null) return;

        const mainSelect = document.getElementById(
          `Option-${this.sectionId}-${index}`
        );

        if (!mainSelect) return;

        const value = stickySelect.value;
        const productInfo = mainSelect.closest("product-info");
        const hadPendingRequest = Boolean(productInfo?.pendingRequestUrl);

        // Update main native select first.
        this.syncSelectValue(mainSelect, value);

        // Update main visual custom picker immediately.
        const mainCell = mainSelect.closest(".product-form__input--dropdown");
        this.syncCustomOptionDisplay(mainCell, value);
        const selectedVariant = this.syncCurrentVariantToMainForm(
          "sticky-option"
        );

        this.logDebug("mirror before events", {
          index,
          value,
          hadPendingRequest,
          directPublish: this.shouldUseDirectVariantPublish(),
          selectedVariant: this.getVariantDebugState(selectedVariant),
          main: this.getSelectDebugState(mainSelect),
          sticky: this.getSelectDebugState(stickySelect),
          selectedOptionValues: this.getSelectedOptionValues(),
          formVariantId: this.getMainProductFormVariantId(),
        });

        if (this.shouldUseDirectVariantPublish()) {
          // Safari can report stale selectedOptions from synthetic bubbled
          // change events here, which makes the PDP fetch the first variant.
          // Publish the exact option-value payload instead and keep the DOM
          // quiet so Klaviyo does not read stale form state from the event.
          this.publishOptionValueSelection(
            mainSelect,
            "safari-direct",
            sourceEvent
          );
        } else {
          // Dispatch both events so whichever theme listener is used catches it.
          mainSelect.dispatchEvent(new Event("input", { bubbles: true }));
          mainSelect.dispatchEvent(new Event("change", { bubbles: true }));

          if (!hadPendingRequest && !productInfo?.pendingRequestUrl) {
            this.publishOptionValueSelection(mainSelect, "fallback-after-change");
          }
        }

        this.logDebug("mirror after events", {
          index,
          value,
          pendingRequestUrl: productInfo?.pendingRequestUrl || null,
          main: this.getSelectDebugState(mainSelect),
          selectedOptionValues: this.getSelectedOptionValues(),
          formVariantId: this.getMainProductFormVariantId(),
          selectedVariant: this.getVariantDebugState(selectedVariant),
        });

        // Re-sync sticky from main after dispatch, so both sides stay visually aligned.
        this.syncStickyFromMainSelect(mainSelect);
      }
      /* ---------- Virtual variant dropdown ----------
         Handles its own open/close (same .active+.visible pattern),
         and proxies option clicks to the main virtual picker so
         the section-swap fetch logic isn't duplicated. */

      setupVirtualVariantDropdown() {
        const wrap = this.querySelector("[data-sticky-virtual-dropdown]");
        if (!wrap) return;

        wrap.addEventListener(
          "click",
          (e) => {
            if (e.target.closest("[data-sticky-virtual-option]")) return;
            e.stopPropagation();
            e.stopImmediatePropagation();
            const isOpen = wrap.classList.contains("active");
            this.closeAllDropdowns();
            if (!isOpen) this.openSelect(wrap);
          },
          true
        );

        wrap.querySelectorAll("[data-sticky-virtual-option]").forEach((opt) => {
          opt.addEventListener(
            "click",
            (e) => {
              e.stopPropagation();
              e.stopImmediatePropagation();
              e.preventDefault();

              const url = opt.dataset.url;
              const handle = opt.dataset.handle;
              if (!url) return;

              // Reuse the main virtual picker's logic — find its
              // matching option element and click it. It owns the
              // section-swap fetch + URL update.
              const mainVirtualOption = document.querySelector(
                `[data-virtual-option][data-handle="${handle}"]`
              );
              if (mainVirtualOption) {
                mainVirtualOption.click();
                this.closeSelect(wrap);
                return;
              }

              // Fallback if main virtual picker isn't on the page.
              if (url !== window.location.pathname) {
                window.location.href = url;
              }
              this.closeSelect(wrap);
            },
            true
          );
        });
      }

      /* ---------- Outside-click ---------- */

      _onDocClick(e) {
        if (!this.contains(e.target)) this.closeAllDropdowns();
      }

      /* ---------- Main picker <select> sync ----------
      This is a direct fallback for custom swatch layouts.
      It keeps sticky ATC updated even if the theme pub/sub timing misses
      a visual custom-option click. */

      setupMainPickerDelegatedSync() {
        if (this.mainPickerSyncAbortController) {
          this.mainPickerSyncAbortController.abort();
        }

        this.mainPickerSyncAbortController = new AbortController();

        document.addEventListener(
          "change",
          (event) => {
            const target = event.target instanceof Element ? event.target : null;
            if (!target) return;

            const mainSelect = target.closest(
              `#variant-selects-${this.sectionId} select[id^="Option-${this.sectionId}-"]`
            );

            if (!mainSelect) return;
            if (this.contains(mainSelect)) return;

            requestAnimationFrame(() => {
              this.syncStickyFromMainPicker();
            });
          },
          {
            capture: true,
            signal: this.mainPickerSyncAbortController.signal,
          }
        );

        document.addEventListener(
          "click",
          (event) => {
            const target = event.target instanceof Element ? event.target : null;
            if (!target) return;

            const option = target.closest(
              `#variant-selects-${this.sectionId} .custom-option`
            );

            if (!option) return;
            if (this.contains(option)) return;

            window.setTimeout(() => {
              this.syncStickyFromMainPicker();
            }, 0);
          },
          {
            capture: true,
            signal: this.mainPickerSyncAbortController.signal,
          }
        );
      }

    getSelectIndex(selectEl) {
      if (!selectEl) return null;

      if (selectEl.dataset.stickyOptionIndex !== undefined) {
        return selectEl.dataset.stickyOptionIndex;
      }

      const match = selectEl.id?.match(
        new RegExp(`(?:Option|StickyOption)-${this.sectionId}-(\\d+)`)
      );

      return match ? match[1] : null;
    }

    shouldUseDirectVariantPublish() {
      return this.isSafariBrowser();
    }

    isSafariBrowser() {
      const ua = window.navigator.userAgent;

      return (
        /safari/i.test(ua) &&
        !/chrome|chromium|crios|android|edg|opr|opera|fxios/i.test(ua)
      );
    }

    getCustomOptionValue(optionEl) {
      return (
        optionEl?.dataset?.realValue ||
        optionEl?.dataset?.value ||
        optionEl?.textContent ||
        ""
      ).trim();
    }

    syncSelectValue(selectEl, value) {
      if (!selectEl || value === undefined || value === null) return;

      let selectedIndex = -1;

      Array.from(selectEl.options).forEach((option, index) => {
        const isSelected = option.value === value;

        option.selected = isSelected;
        option.defaultSelected = isSelected;

        if (isSelected) {
          selectedIndex = index;
          option.setAttribute("selected", "selected");
        } else {
          option.removeAttribute("selected");
        }
      });

      if (selectedIndex >= 0) {
        selectEl.selectedIndex = selectedIndex;
      }

      selectEl.value = value;
    }

    syncCustomOptionDisplay(cellEl, value) {
      if (!cellEl || value === undefined || value === null) return;

      let selectedOption = null;

      cellEl.querySelectorAll(".custom-option").forEach((option) => {
        const optionValue = this.getCustomOptionValue(option);
        const isSelected = optionValue === value;

        option.classList.toggle("selected", isSelected);

        if (isSelected) {
          selectedOption = option;
        }
      });

      if (!selectedOption) return;

      const label = cellEl.querySelector(":scope > .custom__label");

      if (label) {
        const dot = selectedOption.querySelector(".color-dot");

        label.innerHTML = "";

        if (dot) {
          label.appendChild(dot.cloneNode(true));
        }

        label.appendChild(document.createTextNode(value));
      }
    }

    syncStickyFromMainSelect(mainSelect) {
      const index = this.getSelectIndex(mainSelect);
      if (index === null) return;

      const stickySelect = this.querySelector(
        `select[data-sticky-option-index="${index}"]`
      );

      if (!stickySelect) return;

      const value = mainSelect.value;

      this.syncSelectValue(stickySelect, value);

      const stickyCell = stickySelect.closest(".sticky-atc__variant");
      this.syncCustomOptionDisplay(stickyCell, value);
    }

    syncStickyFromMainPicker() {
      const mainSelects = Array.from(
        document.querySelectorAll(
          `#variant-selects-${this.sectionId} select[id^="Option-${this.sectionId}-"]`
        )
      );

      mainSelects.forEach((mainSelect) => {
        this.syncStickyFromMainSelect(mainSelect);
      });
    }

    getSelectedOption(mainSelect) {
      if (!mainSelect) return null;

      const options = Array.from(mainSelect.options || []);

      return (
        options.find((option) => option.value === mainSelect.value) ||
        options.find((option) => option.hasAttribute("selected")) ||
        options[mainSelect.selectedIndex] ||
        mainSelect.selectedOptions?.[0] ||
        null
      );
    }

    getSelectedOptionValues() {
      return Array.from(
        document.querySelectorAll(
          `#variant-selects-${this.sectionId} select[id^="Option-${this.sectionId}-"]`
        )
      )
        .map((select) => this.getSelectedOption(select)?.dataset?.optionValueId)
        .filter(Boolean);
    }

    publishOptionValueSelection(mainSelect, reason, sourceEvent) {
      if (
        typeof window.publish !== "function" ||
        !window.PUB_SUB_EVENTS?.optionValueSelectionChange
      ) {
        this.logDebug("publish skipped", {
          reason,
          hasPublish: typeof window.publish === "function",
          hasEventName: Boolean(window.PUB_SUB_EVENTS?.optionValueSelectionChange),
        });
        return;
      }

      const selectedOption = this.getSelectedOption(mainSelect);
      const selectedOptionValues = this.getSelectedOptionValues();

      if (!selectedOption || !selectedOptionValues.length) {
        this.logDebug("publish skipped", {
          reason,
          selectedOption: selectedOption?.value || null,
          selectedOptionValues,
        });
        return;
      }

      this.logDebug("publish fallback", {
        reason,
        selectedOption: selectedOption.value,
        selectedOptionValues,
      });

      window.publish(window.PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event: sourceEvent || this.createSyntheticEvent(mainSelect),
          target: selectedOption,
          selectedOptionValues,
        },
      });
    }

    createSyntheticEvent(target) {
      return {
        target,
        type: "sticky-atc:change",
        preventDefault() {},
        stopPropagation() {},
        stopImmediatePropagation() {},
        isDefaultPrevented: () => false,
        isPropagationStopped: () => false,
        isImmediatePropagationStopped: () => false,
      };
    }

    getStickyVariantData() {
      if (this.variantData) return this.variantData;

      const source = this.querySelector("[data-sticky-variants]");
      if (!source) {
        this.variantData = [];
        return this.variantData;
      }

      try {
        const parsed = JSON.parse(source.textContent || "[]");
        this.variantData = Array.isArray(parsed) ? parsed : [];
      } catch (error) {
        this.variantData = [];
        this.logDebug("variant data parse failed", {
          message: error?.message || String(error),
        });
      }

      return this.variantData;
    }

    getCurrentOptionValues() {
      const mainSelects = Array.from(
        document.querySelectorAll(
          `#variant-selects-${this.sectionId} select[id^="Option-${this.sectionId}-"]`
        )
      ).sort((a, b) => {
        const aIndex = Number(this.getSelectIndex(a) || 0);
        const bIndex = Number(this.getSelectIndex(b) || 0);
        return aIndex - bIndex;
      });

      if (mainSelects.length) {
        return mainSelects.map((select) => select.value);
      }

      return Array.from(
        this.querySelectorAll("select[data-sticky-option-index]")
      )
        .sort((a, b) => {
          const aIndex = Number(this.getSelectIndex(a) || 0);
          const bIndex = Number(this.getSelectIndex(b) || 0);
          return aIndex - bIndex;
        })
        .map((select) => select.value);
    }

    getCurrentVariantFromOptions() {
      const optionValues = this.getCurrentOptionValues();
      if (!optionValues.length) return null;

      return (
        this.getStickyVariantData().find((variant) => {
          const variantOptions = variant?.options || [];
          if (variantOptions.length !== optionValues.length) return false;

          return variantOptions.every(
            (optionValue, index) => optionValue === optionValues[index]
          );
        }) || null
      );
    }

    syncCurrentVariantToMainForm(reason) {
      const variant = this.getCurrentVariantFromOptions();
      if (!variant?.id) {
        this.logDebug("variant form sync skipped", {
          reason,
          optionValues: this.getCurrentOptionValues(),
        });
        return null;
      }

      this.updateMainVariantInputs(variant.id);
      this.updateNotifyVariantId(variant.id);
      this.updateButtonsFromVariant(variant);

      this.logDebug("variant form sync", {
        reason,
        variant: this.getVariantDebugState(variant),
        formVariantId: this.getMainProductFormVariantId(),
      });

      return variant;
    }

    updateMainVariantInputs(variantId) {
      document
        .querySelectorAll(
          `#product-form-${this.sectionId}, #product-form-installment-${this.sectionId}`
        )
        .forEach((form) => {
          const input = form.querySelector('input[name="id"]');
          if (!input) return;

          input.disabled = false;
          input.value = variantId;
          input.setAttribute("value", variantId);
        });
    }

    updateNotifyVariantId(variantId) {
      const productInfo = this.closest("product-info") || document;

      productInfo
        .querySelectorAll(".back-in-stock [data-variant-id]")
        .forEach((button) => {
          button.dataset.variantId = String(variantId);
          button.setAttribute("data-variant-id", String(variantId));
        });
    }

    updateButtonsFromVariant(variant) {
      const liveBtn = document.getElementById(this.targetId);
      const notifyBtn = this.closest("product-info")?.querySelector(
        ".back-in-stock"
      );
      const shortSupply = liveBtn?.dataset?.short_supply === "true";

      if (liveBtn) {
        if (variant.available) {
          liveBtn.style.display = "block";
          liveBtn.removeAttribute("disabled");
          liveBtn.setAttribute("aria-disabled", "false");
          liveBtn.dataset.notify = "false";
        } else if (!shortSupply) {
          liveBtn.style.display = "none";
          liveBtn.dataset.notify = "true";
        } else {
          liveBtn.style.display = "block";
          liveBtn.setAttribute("disabled", "disabled");
          liveBtn.setAttribute("aria-disabled", "true");
          liveBtn.dataset.notify = "true";
        }
      }

      if (notifyBtn) {
        notifyBtn.style.display = variant.available ? "none" : "block";
      }

      this.syncFromMainButton();
    }

    getVariantDebugState(variant) {
      if (!variant) return null;

      return {
        id: variant.id,
        available: variant.available,
        options: variant.options,
      };
    }

    isDebugEnabled() {
      try {
        const params = new URLSearchParams(window.location.search);
        return (
          params.has("sticky_atc_debug") ||
          window.localStorage?.getItem("stickyAtcDebug") === "true"
        );
      } catch (_error) {
        return false;
      }
    }

    logDebug(label, details = {}) {
      if (!this.debug) return;

      console.log(`[sticky-atc] ${label}`, {
        sectionId: this.sectionId,
        path: window.location.pathname,
        userAgent: window.navigator.userAgent,
        ...details,
      });
    }

    getSelectDebugState(selectEl) {
      if (!selectEl) return null;

      const selectedOption = this.getSelectedOption(selectEl);

      return {
        id: selectEl.id || null,
        value: selectEl.value,
        selectedIndex: selectEl.selectedIndex,
        selectedOptionValue: selectedOption?.value || null,
        selectedOptionText: selectedOption?.textContent?.trim() || null,
        selectedOptionValueId: selectedOption?.dataset?.optionValueId || null,
        selectedAttributes: Array.from(selectEl.options || [])
          .filter((option) => option.hasAttribute("selected"))
          .map((option) => option.value),
        selectedProperties: Array.from(selectEl.options || [])
          .filter((option) => option.selected)
          .map((option) => option.value),
      };
    }

    getMainProductFormVariantId() {
      const productForm = document.getElementById(this.productFormId);

      return (
        productForm?.querySelector('input[name="id"]')?.value ||
        this.closest("product-info")
          ?.querySelector('form[action*="/cart/add"] input[name="id"]')
          ?.value ||
        null
      );
    }

    getDebugState() {
      return {
        selectedOptionValues: this.getSelectedOptionValues(),
        formVariantId: this.getMainProductFormVariantId(),
        targetButtonDisabled: document
          .getElementById(this.targetId)
          ?.hasAttribute("disabled"),
      };
    }

      /* ---------- Variant change sync ---------- */

      setupVariantChangeListener() {
        if (typeof window.subscribe !== "function" || !window.PUB_SUB_EVENTS) return;
        // Guard against double-subscribe.
        if (typeof this.unsubscribeVariantChange === "function") return;

        this.unsubscribeVariantChange = window.subscribe(
          window.PUB_SUB_EVENTS.variantChange,
          (event) => {
            if (event?.data?.sectionId !== this.sectionId) return;
            this.handleVariantChange(event.data);
          }
        );
      }

      handleVariantChange({ html, variant }) {
        if (!html) return;

        this.logDebug("variantChange", {
          variantId: variant?.id || null,
          variantAvailable: variant?.available ?? null,
          variantOptions: variant?.options || null,
          selectedOptionValues: this.getSelectedOptionValues(),
          formVariantId: this.getMainProductFormVariantId(),
        });

        // Mirror updated price
        const sourcePrice = html.getElementById(`price-${this.sectionId}`);
        const targetPrice = document.getElementById(
          `StickyAtcPrice-${this.sectionId}`
        );
        if (sourcePrice && targetPrice) {
          targetPrice.innerHTML = sourcePrice.innerHTML;
        }

        this.updateStickyDropdownsFromVariant(variant);
        requestAnimationFrame(() => {
          this.setupMainButtonObserver();
          this.syncStickyFromMainPicker();
          this.syncFromMainButton();
        });
        // Pass the freshly-fetched section html so we can read button
        // state from the NEW markup, not the live (potentially stale)
        // attributes on the existing button element.
        this.syncFromMainButton(html);
      }

      updateStickyDropdownsFromVariant(variant) {
        if (!variant || !variant.options) return;

        variant.options.forEach((optionValue, index) => {
          const stickySelect = this.querySelector(
            `select[data-sticky-option-index="${index}"]`
          );
          if (!stickySelect) return;
          if (stickySelect.value !== optionValue) {
            stickySelect.value = optionValue;
          }

          const cell = stickySelect.closest(".sticky-atc__variant");
          if (!cell) return;

          let selectedOpt = null;
          cell.querySelectorAll(".custom-option").forEach((opt) => {
            const isSelected =
              (opt.dataset.realValue || opt.dataset.value || "").trim() ===
              optionValue;
            opt.classList.toggle("selected", isSelected);
            if (isSelected) selectedOpt = opt;
          });

          if (selectedOpt) {
            this.updateStickyLabel(cell, selectedOpt, optionValue);
          }
        });
      }

      syncFromMainButton(freshHtml) {
        const stickySubmit = this.querySelector("[data-sticky-atc-submit]");
        if (!stickySubmit) return;

        if (!freshHtml) {
          const productInfo = this.closest("product-info");
          if (productInfo && productInfo.pendingRequestUrl) return;
        }

        const source = freshHtml
          ? freshHtml.getElementById(this.targetId)
          : document.getElementById(this.targetId);

        if (!source) {
          stickySubmit.setAttribute("disabled", "disabled");
          stickySubmit.setAttribute("aria-disabled", "true");
          return;
        }

        // Whether the button itself is hidden (server-render path for
        // some unavailable states). The live element uses style.display;
        // the fresh html element typically does not have inline styles
        // applied yet, so this mostly matters for the initial-sync call.
        const isHidden = source.style?.display === "none";

        // Disabled state. We intentionally do NOT read `data-disabled`
        // because that attribute is set by Liquid at server render time
        // and is never updated by product-form.js's toggleSubmitButton.
        // After the first out-of-stock variant, it stays "disabled" for
        // the rest of the page lifetime, which made the sticky button
        // stick on "Sold out" even after switching back to in-stock.
        // The reliable signals are `disabled` and `aria-disabled`,
        // both of which toggleSubmitButton actively maintains.
        const isDisabled =
          source.hasAttribute("disabled") ||
          source.getAttribute("aria-disabled") === "true";

        // Pull text from the button's first span (matches
        // product-form.js's submitButtonText selector).
        const sourceText = source.querySelector("span")?.textContent?.trim();
        const stickyText = stickySubmit.querySelector(".sticky-atc__submit-text");
        if (sourceText && stickyText) stickyText.textContent = sourceText;

        if (isHidden || isDisabled) {
          stickySubmit.setAttribute("disabled", "disabled");
          stickySubmit.setAttribute("aria-disabled", "true");
        } else {
          stickySubmit.removeAttribute("disabled");
          stickySubmit.removeAttribute("aria-disabled");
        }
      }
    }
  );
}
